import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'
import { BarChart2, Table2, Plus, Banknote } from 'lucide-react'
import { defaultFromDaysAgo, toDatetimeLocalValue } from '../utils/investigationStepBrowse'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) => '$' + (Number(n) ?? 0).toLocaleString()

/**
 * Browse metering and gaming data for an investigation case.
 * Pre-filters by case member and date window when provided.
 */
export default function CaseBrowseData({
  caseId,
  onAdded,
  defaultVenue,
  defaultMemberId,
  defaultDays = 90,
  initialSubTab = 'sessions',
  stepId = null,
  stepTitle = '',
  presetKey = 0,
}) {
  const [venues, setVenues] = useState([])
  const [venue, setVenue] = useState('')
  const [memberId, setMemberId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [amountLt, setAmountLt] = useState('')
  const [amountGt, setAmountGt] = useState('')
  const [subTab, setSubTab] = useState(initialSubTab)
  const [sessions, setSessions] = useState([])
  const [meterReadings, setMeterReadings] = useState([])
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedSessions, setSelectedSessions] = useState(new Set())
  const [selectedMeter, setSelectedMeter] = useState(new Set())
  const [selectedPayouts, setSelectedPayouts] = useState(new Set())
  const [adding, setAdding] = useState(false)
  const autoLoadedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && !cancelled) setVenues(j.data)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  // Reset filters when case or browse preset changes
  useEffect(() => {
    autoLoadedRef.current = false
    setVenue('')
    setMemberId(defaultMemberId ? String(defaultMemberId).trim() : '')
    setSubTab(initialSubTab)
    const now = new Date()
    setTo(toDatetimeLocalValue(now))
    setFrom(defaultFromDaysAgo(defaultDays || 90))
    setAmountLt('')
    setAmountGt('')
    setSessions([])
    setMeterReadings([])
    setPayouts([])
    setSelectedSessions(new Set())
    setSelectedMeter(new Set())
    setSelectedPayouts(new Set())
    setError(null)
  }, [caseId, defaultMemberId, defaultDays, initialSubTab, presetKey])

  useEffect(() => {
    if (venue) return
    // defaultVenue may be a venue_id or display name — prefer matching by venue_id, then name
    if (defaultVenue) {
      const match = venues.find((v) =>
        (v.venue_id || String(v.id)) === defaultVenue ||
        (v.name || v.venue_name || '') === defaultVenue
      )
      setVenue(match ? (match.venue_id || String(match.id)) : defaultVenue)
      return
    }
    if (venues.length === 1) {
      setVenue(venues[0].venue_id || String(venues[0].id))
    }
  }, [caseId, defaultVenue, venues, venue, presetKey])

  // venue state holds the platform venue_id (not display name)
  const venueOptions = venues.map((v) => ({
    venue_id: v.venue_id || String(v.id),
    name: v.name || v.venue_name || String(v.id),
    extract_name: v.gaming_extract_venue_name || null,
  }))
  const venueFilter = venue   // platform venue_id
  const memberFilter = memberId.trim()

  const loadSessions = useCallback(async () => {
    if (!venueFilter && !memberFilter && !from) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue_id', venueFilter)
      if (memberFilter) qs.set('member_id', memberFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      qs.set('limit', '200')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/sessions?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load sessions')
      setSessions(j.data || [])
      setSelectedSessions(new Set())
    } catch (e) {
      setError(e.message)
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, memberFilter, from, to])

  const loadMeterReadings = useCallback(async () => {
    if (!venueFilter && !memberFilter && !from) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue_id', venueFilter)
      if (memberFilter) qs.set('member_id', memberFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      qs.set('limit', '200')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/egms/readings?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load meter readings')
      setMeterReadings(j.data || [])
      setSelectedMeter(new Set())
    } catch (e) {
      setError(e.message)
      setMeterReadings([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, memberFilter, from, to])

  const loadPayouts = useCallback(async () => {
    // Monitoring payouts do not require a venue — member or date range is enough
    if (!memberFilter && !from && !to && !venueFilter) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue', venueFilter)
      if (memberFilter) qs.set('member_id', memberFilter)
      // Monitoring API uses dateFrom/dateTo (not from/to)
      if (from) qs.set('dateFrom', from)
      if (to) qs.set('dateTo', to)
      qs.set('limit', '200')
      const r = await authFetch(getLocalApiUrl(`/api/monitoring/payouts?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load payouts')
      let rows = j.data || []
      // Client-side amount filter (monitoring API does not have amount_lt/gt params)
      if (amountGt) rows = rows.filter((p) => Number(p.amount) > Number(amountGt))
      if (amountLt) rows = rows.filter((p) => Number(p.amount) < Number(amountLt))
      setPayouts(rows)
      setSelectedPayouts(new Set())
    } catch (e) {
      setError(e.message)
      setPayouts([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, memberFilter, from, to, amountLt, amountGt])

  const load = useCallback(() => {
    if (subTab === 'sessions') loadSessions()
    else if (subTab === 'meter') loadMeterReadings()
    else if (subTab === 'payouts') loadPayouts()
  }, [subTab, loadSessions, loadMeterReadings, loadPayouts])

  useEffect(() => {
    // Payouts tab can auto-load with just a date range and member; other tabs need a venue
    const canAutoLoad = subTab === 'payouts'
      ? (memberFilter || from || to || venueFilter)
      : subTab === 'meter'
        ? ((venueFilter || memberFilter) && from && to)
        : (venueFilter && from && to)
    if (!canAutoLoad) return
    if (autoLoadedRef.current) return
    autoLoadedRef.current = true
    load()
  }, [venueFilter, from, to, memberFilter, subTab, load, presetKey])

  const toggleSession = (pk) => {
    setSelectedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(pk)) next.delete(pk)
      else next.add(pk)
      return next
    })
  }

  const toggleMeter = (key) => {
    setSelectedMeter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const togglePayout = (pk) => {
    setSelectedPayouts((prev) => {
      const next = new Set(prev)
      if (next.has(pk)) next.delete(pk)
      else next.add(pk)
      return next
    })
  }

  const handleAddToEvidence = useCallback(async () => {
    if (!caseId) return
    const items = []
    if (subTab === 'sessions') {
      for (const pk of selectedSessions) {
        const s = sessions.find((x) => String(x.SessionPK) === String(pk))
        if (s)
          items.push({
            type: 'session',
            payload: s,
            source_table: 'gaming_sessions',
            SessionPK: s.SessionPK,
            StartTime: s.StartTime,
          })
      }
    } else if (subTab === 'payouts') {
      for (const pk of selectedPayouts) {
        const p = payouts.find((x) => String(x.id) === String(pk))
        if (p)
          items.push({
            type: 'payout',
            payload: p,
            source_table: 'payout_records',
            payout_id: p.id,
            timestamp: p.timestamp,
          })
      }
    } else {
      for (const key of selectedMeter) {
        const m = meterReadings.find((x) => `${x.EgmPK}-${x.SnapshotTime}` === key)
        if (m)
          items.push({
            type: 'meter_reading',
            payload: m,
            source_table: 'egm_meter_readings',
            SnapshotTime: m.SnapshotTime,
          })
      }
    }
    if (items.length === 0) return
    setAdding(true)
    try {
      const body = { items }
      if (stepId) body.step_id = stepId
      const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to add evidence')
      setSelectedSessions(new Set())
      setSelectedMeter(new Set())
      setSelectedPayouts(new Set())
      onAdded?.(j.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setAdding(false)
    }
  }, [caseId, stepId, subTab, selectedSessions, selectedMeter, selectedPayouts, sessions, meterReadings, payouts, onAdded])

  const selectedCount = subTab === 'sessions' ? selectedSessions.size : subTab === 'payouts' ? selectedPayouts.size : selectedMeter.size
  const hasSelection = selectedCount > 0

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Browse gaming and payout data for this investigation. Filters default to the case member and last {defaultDays || 90} days when available.
        {stepId ? ' Selected rows attach to the open investigation step.' : ' Selected rows attach to case evidence.'}
      </div>

      {stepId && stepTitle && (
        <div className="text-xs font-semibold rounded-md px-3 py-2" style={{ background: P.priM, color: P.pri }}>
          Attaching to step: {stepTitle}
        </div>
      )}

      <div className="card p-4">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Venue</label>
            <select
              className="input-field w-full text-sm"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            >
              <option value="">All venues</option>
              {venueOptions.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>
                  {v.name}{v.extract_name ? ` (${v.extract_name})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Member #</label>
            <input
              type="text"
              className="input-field w-full text-sm font-mono"
              placeholder="Case member"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">From</label>
            <input
              type="datetime-local"
              className="input-field w-full text-sm"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">To</label>
            <input
              type="datetime-local"
              className="input-field w-full text-sm"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          {subTab === 'payouts' && (
            <>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payout &lt; $</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 500"
                  className="input-field w-24 text-sm"
                  value={amountLt}
                  onChange={(e) => setAmountLt(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payout &gt; $</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 100"
                  className="input-field w-24 text-sm"
                  value={amountGt}
                  onChange={(e) => setAmountGt(e.target.value)}
                />
              </div>
            </>
          )}
          <div className="flex gap-2 col-span-2 sm:col-span-1">
            <button
              type="button"
              onClick={load}
              className="btn-primary text-sm py-2 px-4"
            >
              Apply
            </button>
            {hasSelection && (
              <button
                type="button"
                onClick={handleAddToEvidence}
                disabled={adding}
                className="text-sm py-2 px-4 rounded-md border font-semibold flex items-center gap-1"
                style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
              >
                {adding ? 'Adding…' : <><Plus size={14} /> Add {selectedCount}{stepId ? ' to step' : ''}</>}
              </button>
            )}
          </div>
        </div>
        {memberFilter && (
          <div className="mt-2 text-[11px] text-slate-500">
            Showing data for member <span className="font-mono font-semibold text-slate-700">{memberFilter}</span>
            {from && to ? ` · ${from.slice(0, 10)} to ${to.slice(0, 10)}` : ''}
          </div>
        )}
      </div>

      <div className="flex gap-2 border-b border-slate-200 mb-2">
        <button
          type="button"
          onClick={() => { setSubTab('sessions'); setError(null); if (venueFilter) loadSessions(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'sessions' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'sessions' ? P.pri : '#94a3b8' }}
        >
          <Table2 size={14} /> Gaming Sessions
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('meter'); setError(null); if (venueFilter || memberFilter) loadMeterReadings(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'meter' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'meter' ? P.pri : '#94a3b8' }}
        >
          <BarChart2 size={14} /> Meter Readings
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('payouts'); setError(null); if (venueFilter) loadPayouts(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'payouts' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'payouts' ? P.pri : '#94a3b8' }}
        >
          <Banknote size={14} /> Payouts
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="card overflow-hidden">
        {subTab === 'sessions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">SessionPK</th>
                  <th className="px-3 py-2 text-left">MemberID</th>
                  <th className="px-3 py-2 text-left">EgmName</th>
                  <th className="px-3 py-2 text-left">Venue</th>
                  <th className="px-3 py-2 text-left">StartTime</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && sessions.map((r) => (
                  <tr key={r.SessionPK} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedSessions.has(String(r.SessionPK))}
                        onChange={() => toggleSession(String(r.SessionPK))}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">{r.SessionPK}</td>
                    <td className="px-3 py-2">{r.MemberID || '—'}</td>
                    <td className="px-3 py-2">{r.EgmName || '—'}</td>
                    <td className="px-3 py-2">{r.Venue || '—'}</td>
                    <td className="px-3 py-2">{r.StartTime ? new Date(r.StartTime).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">{fmt$(r.Turnover)}</td>
                    <td className="px-3 py-2">{fmt$(r.CreditWin)}</td>
                  </tr>
                ))}
                {!loading && sessions.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No sessions for these filters. Adjust member, dates, or venue and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subTab === 'meter' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">SnapshotTime</th>
                  <th className="px-3 py-2 text-left">EgmPK</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                  <th className="px-3 py-2 text-left">Stroke</th>
                  <th className="px-3 py-2 text-left">CashIn</th>
                  <th className="px-3 py-2 text-left">CashDrop</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && meterReadings.map((r) => {
                  const key = `${r.EgmPK}-${r.SnapshotTime}`
                  return (
                    <tr key={key} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedMeter.has(key)}
                          onChange={() => toggleMeter(key)}
                        />
                      </td>
                      <td className="px-3 py-2">{r.SnapshotTime ? new Date(r.SnapshotTime).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 font-mono">{r.EgmPK}</td>
                      <td className="px-3 py-2">{fmt$(r.Turnover)}</td>
                      <td className="px-3 py-2">{fmt$(r.CreditWin)}</td>
                      <td className="px-3 py-2">{r.Stroke ?? '—'}</td>
                      <td className="px-3 py-2">{fmt$(r.CashIn)}</td>
                      <td className="px-3 py-2">{fmt$(r.CashDrop)}</td>
                    </tr>
                  )
                })}
                {!loading && meterReadings.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No meter readings. Select venue and/or member, set dates, and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subTab === 'payouts' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">Time</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Member / Visitor</th>
                  <th className="px-3 py-2 text-left">Venue</th>
                  <th className="px-3 py-2 text-left">Method</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Payout ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && payouts.map((r) => {
                  const identity = r.memberName || r.memberId
                    ? `${r.memberName ? r.memberName + ' — ' : ''}${r.memberId || ''}`
                    : r.visitorId ? `Visitor ${r.visitorId}` : 'Unidentified'
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedPayouts.has(String(r.id))}
                          onChange={() => togglePayout(String(r.id))}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">{r.amount != null ? fmt$(r.amount) : '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{identity}</td>
                      <td className="px-3 py-2 text-xs">{r.venue || '—'}</td>
                      <td className="px-3 py-2 text-xs capitalize">{r.paymentMethod || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.complianceStatus
                          ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.complianceStatus === 'COMPLIANT' ? 'bg-green-100 text-green-700' : r.complianceStatus === 'NON_COMPLIANT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.complianceStatus}</span>
                          : <span className="text-slate-400">—</span>
                        }
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.id}</td>
                    </tr>
                  )
                })}
                {!loading && payouts.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No payouts for these filters. Adjust member, dates, or venue and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
